import * as THREE from 'three'
//------ 线性零部件阵列离线包定义持久化 开始------
import {
  GlbHierarchyPersistenceMode,
  type GlbHierarchyNode,
} from '../../imported-model/loaders/glb'
//------ 线性零部件阵列离线包定义持久化 结束------
import type { Matrix4Tuple } from '../../imported-model/loaders/types'
import {
  createImportedBodyOccurrenceBindingId,
  createImportedBodyOwnershipLookupKeys,
  resolveImportedBodyOccurrenceNodeId,
} from '../../imported-model/utils'
import { resolveImportedBrepGeometryObjectAssetScope } from '../../imported-model/hydration'
import { splitAssemblyNodePath } from '../../assembly/utils/AssemblyNodePath'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyDisplayNodeBodyBinding,
  PuppyDisplayNodeBinding,
  PuppyExportAssemblySemanticSource,
  PuppyVisibleBodyTopologyAsset,
  PuppyVisibleModelExportRoot,
} from './types'
import { PUPPY_PACKAGE_VERSION } from './types'
import { cloneAssemblyNodeMeta } from './assemblyNodeMeta'
import { PuppyAssemblyDisplayTreeMaterializer } from './PuppyAssemblyDisplayTreeMaterializer'
import {
  remapPuppyAssemblyMateDocumentNodeReferences,
} from './remapPuppyAssemblyMateDocument'
import { resolvePuppyAssemblyNodeReferenceAlias } from './remapPuppyAssemblyNodeReferences'
import {
  readPuppyDatumExtensionFromAssemblyExtensions,
  remapPuppyDatumExtensionNodeReferences,
  writePuppyDatumExtensionToAssemblyExtensions,
} from './puppyDatumExtension'
import { createPuppyBodyTopologyAssetKey } from './bodyTopologyAssetKey'

/**
 * 从 hierarchy 派生 assembly.json 时的中间结果。
 */
interface PuppyHierarchyAssemblyBuildResult {
  /** hierarchy 转换后的 assembly.json。 */
  readonly assembly: PuppyAssemblyDocument
  /** bodyId / bodyBindingId 到 assembly node ID 的归属索引。 */
  readonly ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>
  /** 最终装配节点到本次导出事务中全部运行时 Object3D UUID 的绑定。 */
  readonly runtimeSourceObjectIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>>
}

/**
 * scoped hierarchy 按 Mate 权威节点折叠后的装配图。
 */
interface PuppyCanonicalizedHierarchyAssemblyGraph {
  /** 最终写入 assembly.json 的根节点 ID。*/
  readonly rootNodeId: string
  /** 最终写入 assembly.json 的节点列表。*/
  readonly nodes: PuppyAssemblyNode[]
  /** 最终写入 assembly.json 的父子关系。*/
  readonly childIdsByNodeId: Record<string, string[]>
  /** 已同步到最终节点 ID 的 body 归属索引。*/
  readonly ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>
  /** scoped 内嵌根节点 ID 到最终节点 ID 的别名。*/
  readonly aliasByNodeId: Readonly<Record<string, string>>
}

/**
 * 节点身份索引中出现冲突时使用 null 标记为不可匹配。
 */
type PuppyAssemblyNodeIdentityIndexValue = string | null

/**
 * body 归属索引中出现冲突时使用 null 标记为不可匹配。
 */
type PuppyBodyOwnerIndexValue = string | null

/**
 * imported BRep 几何对象 ID 前缀。
 */
const IMPORTED_BREP_GEOMETRY_OBJECT_PREFIX = 'imported-brep:'

/**
 * `.puppy` 内部 zip entry 资产作用域分隔符。
 */
const IMPORTED_BREP_ZIP_ENTRY_SEPARATOR = '::zip-entry::'

/**
 * 单个 display.glb 节点的语义归属结果。
 */
interface PuppyDisplayOwnership {
  /** display.glb 节点 ID。 */
  readonly displayNodeId: string
  /** 当前 display 节点归属的 assembly node ID。 */
  readonly ownerNodeId: string
}

/**
 * display 归属落盘后的绑定快照。
 */
interface PuppyDisplayOwnershipBindingResult {
  /** 已写入权威 assemblyNodeId 与 occurrence 级 bodyBindingId 的 display 绑定。 */
  readonly nodeBindings: PuppyDisplayNodeBinding[]
  /** 与 display bodyBindingId 保持一致的 body 拓扑资产。 */
  readonly bodyTopologyAssets: PuppyVisibleBodyTopologyAsset[]
}

/**
 * 解析可用于离线包导出的权威 hierarchy。
 *
 * @param source 调用方传入的语义源。
 * @returns 权威 hierarchy；缺省时返回 null。
 */
function resolveExportHierarchy(
  source: PuppyExportAssemblySemanticSource | null | undefined,
): GlbHierarchyNode | null {
  if (!source) {
    return null
  }
  if (source.kind === 'imported-model-result') {
    return source.result.hierarchy
  }
  return source.hierarchy
}

/**
 * 判断当前 hierarchy 节点是否为 SolidWorks 语义中的实体节点。
 *
 * SolidWorks 的 Body 属于零件内部的“实体”内容，不是可作为装配组件移动的零件实例。
 * 因此即使历史元数据残留了 `semanticKind: part`，导出 `.puppy` 权威装配树时也必须跳过它。
 *
 * @param node hierarchy 节点。
 * @returns 节点是否为实体节点。
 */
function isSolidBodyHierarchyNode(node: GlbHierarchyNode): boolean {
  return node.nodeType.trim().toLowerCase() === 'body'
}

/**
 * 判断 hierarchy 节点是否是 `.puppy` 需要持久化的装配语义节点。
 *
 * @param node hierarchy 节点。
 * @param isRoot 当前节点是否为根节点。
 * @returns 可导出的语义类型；非语义节点返回 undefined。
 */
function resolveExportableSemanticKind(
  node: GlbHierarchyNode,
  isRoot: boolean,
): Extract<NonNullable<PuppyAssemblyNode['semanticKind']>, 'assembly' | 'part'> | undefined {
  //------ 线性零部件阵列离线包定义持久化 开始------
  // 派生 hierarchy 只服务设计树显示；其 Group 和实例都由阵列定义在加载后重建。
  if (node.meta?.persistenceMode === GlbHierarchyPersistenceMode.RuntimeDerived) {
    return undefined
  }
  //------ 线性零部件阵列离线包定义持久化 结束------
  if (isSolidBodyHierarchyNode(node)) {
    return undefined
  }

  const semanticKind = node.meta?.semanticKind
  if (semanticKind === 'assembly' || semanticKind === 'part') {
    return semanticKind
  }

  /**
   * hierarchy 根节点即使缺少明确语义，也必须保留为文档根。
   * 这样调用方传入非标准 hierarchy 时不会丢失整棵树的入口。
   */
  return isRoot ? 'assembly' : undefined
}

/**
 * 创建可序列化且尽量稳定的 assembly node ID。
 *
 * @param node hierarchy 节点。
 * @param usedNodeIds 已使用的节点 ID 集合。
 * @returns 去重后的节点 ID。
 */
function createAssemblyNodeId(node: GlbHierarchyNode, usedNodeIds: Set<string>): string {
  const baseId = node.meta?.assemblyNodeId?.trim()
    || node.path?.trim()
    || node.name?.trim()
    || node.objectId?.trim()
    || `assembly-node-${usedNodeIds.size + 1}`
  let candidate = baseId
  let sequence = 2
  while (usedNodeIds.has(candidate)) {
    candidate = `${baseId}#${sequence}`
    sequence += 1
  }
  usedNodeIds.add(candidate)
  return candidate
}

/**
 * 创建 assembly node 扩展字段。
 *
 * @param node hierarchy 节点。
 * @returns 可写入 assembly.json 的扩展字段。
 */
function createAssemblyNodeExtensions(node: GlbHierarchyNode): Record<string, unknown> | undefined {
  const selectionSourceObjectId = node.meta?.selectionObjectId?.trim()
  return selectionSourceObjectId
    ? { selectionSourceObjectId }
    : undefined
}

/**
 * 将一个 hierarchy 节点转换为 PuppyAssemblyNode。
 *
 * @param input 转换输入。
 * @returns assembly node。
 */
/**
 * 创建可写入离线包装配节点的 hierarchy meta。
 *
 * @param node hierarchy 节点。
 * @param nodeId 当前离线包装配节点 ID。
 * @returns 保留最终稳定装配节点 ID 的 meta。
 */
function createAssemblyNodeMeta(
  node: GlbHierarchyNode,
  nodeId: string,
  semanticKind: PuppyAssemblyNode['semanticKind'],
): PuppyAssemblyNode['meta'] {
  const meta = cloneAssemblyNodeMeta(node.meta)
  return meta
    ? {
        ...meta,
        assemblyNodeId: nodeId,
      }
    : undefined
}

function createAssemblyNodeFromHierarchy(input: {
  readonly node: GlbHierarchyNode
  readonly nodeId: string
  readonly parentNodeId: string | null
  readonly semanticKind: Extract<NonNullable<PuppyAssemblyNode['semanticKind']>, 'assembly' | 'part'>
  readonly sourceObjectLocalTransforms: Readonly<Record<string, Matrix4Tuple>>
}): PuppyAssemblyNode {
  return {
    nodeId: input.nodeId,
    parentNodeId: input.parentNodeId,
    name: input.node.name,
    nodeType: input.node.nodeType,
    path: input.node.path,
    selectable: input.node.selectable,
    semanticKind: input.semanticKind,
    sourceObjectId: input.node.objectId,
    transform: clonePuppyAssemblyNodeTransform(input.sourceObjectLocalTransforms[input.node.objectId]),
    meta: createAssemblyNodeMeta(input.node, input.nodeId, input.semanticKind),
    displayNodeIds: [],
    bodyBindingIds: [],
    geometryObjectIds: [],
    extensions: createAssemblyNodeExtensions(input.node),
  }
}

/**
 * 将字符串加入索引，空值会被忽略。
 *
 * @param index 目标索引。
 * @param key 待加入 key。
 * @param nodeId 归属节点 ID。
 */
function setResolvedOwnerIndexValue(
  index: Map<string, PuppyBodyOwnerIndexValue>,
  key: string | undefined,
  nodeId: string,
): void {
  if (!key) {
    return
  }

  const existingNodeId = index.get(key)
  if (existingNodeId === undefined) {
    index.set(key, nodeId)
    return
  }
  if (existingNodeId !== nodeId) {
    index.set(key, null)
  }
}

/**
 * 将字符串加入 body 归属索引，空值会被忽略。
 *
 * @param index 目标索引。
 * @param key 待加入 key。
 * @param nodeId 归属节点 ID。
 */
function setOwnerIndexValue(
  index: Map<string, PuppyBodyOwnerIndexValue>,
  key: string | undefined,
  nodeId: string,
): void {
  createImportedBodyOwnershipLookupKeys(key).forEach((lookupKey) => {
    setResolvedOwnerIndexValue(index, lookupKey, nodeId)
  })
}

/**
 * 创建 BRep 资产作用域与 body lookup key 的组合索引键。
 *
 * @param assetScopeKey BRep 资产作用域 key。
 * @param bodyLookupKey body 归属 lookup key。
 * @returns 组合后的内部索引键。
 */
function createAssetScopedBodyOwnerKey(assetScopeKey: string, bodyLookupKey: string): string {
  return `asset-scope:${assetScopeKey}::body:${bodyLookupKey}`
}

/**
 * 从 BRep 资产作用域生成可兼容 zip entry 与外层文件 UUID 的 lookup key。
 *
 * @param value BRep 资产作用域或外部文件 UUID。
 * @returns 去重后的资产作用域 lookup key。
 */
function createImportedBrepAssetScopeLookupKeys(value: unknown): string[] {
  const normalizedValue = normalizeOptionalText(value)
  if (!normalizedValue) {
    return []
  }

  const keys = [normalizedValue]
  const zipEntryIndex = normalizedValue.indexOf(IMPORTED_BREP_ZIP_ENTRY_SEPARATOR)
  if (zipEntryIndex > 0) {
    keys.push(normalizedValue.slice(0, zipEntryIndex))
  }

  return Array.from(new Set(keys))
}

/**
 * 从任意运行时 ID 中提取 imported-brep 的资产作用域 lookup key。
 *
 * @param value 待解析的 bodyBindingId 或 geometryObjectId。
 * @returns BRep 资产作用域 lookup key。
 */
function extractImportedBrepAssetScopeLookupKeys(value: unknown): string[] {
  const normalizedValue = normalizeOptionalText(value)
  if (!normalizedValue) {
    return []
  }

  const prefixIndex = normalizedValue.indexOf(IMPORTED_BREP_GEOMETRY_OBJECT_PREFIX)
  if (prefixIndex < 0) {
    return []
  }

  const importedBrepGeometryObjectId = normalizedValue.slice(prefixIndex)
  const assetScope = resolveImportedBrepGeometryObjectAssetScope(importedBrepGeometryObjectId)
  return createImportedBrepAssetScopeLookupKeys(assetScope)
}

/**
 * 读取 hierarchy body 条目所属的 BRep 资产作用域。
 *
 * @param input 当前 hierarchy 节点与 body 条目。
 * @returns 可用于精确 body owner 匹配的资产作用域 key。
 */
function createHierarchyBodyAssetScopeLookupKeys(input: {
  readonly node: GlbHierarchyNode
  readonly bodyBindingId?: string
  readonly bodyId?: string
  readonly label?: string
}): string[] {
  return Array.from(new Set([
    ...createImportedBrepAssetScopeLookupKeys(input.node.meta?.externalFileUuid),
    ...extractImportedBrepAssetScopeLookupKeys(input.bodyBindingId),
    ...extractImportedBrepAssetScopeLookupKeys(input.bodyId),
    ...extractImportedBrepAssetScopeLookupKeys(input.label),
  ]))
}

/**
 * 为指定 body 身份追加资产作用域级归属索引。
 *
 * @param input 索引写入输入。
 */
function setAssetScopedOwnerIndexValue(input: {
  readonly ownerNodeIdByBodyKey: Map<string, PuppyBodyOwnerIndexValue>
  readonly key: string | undefined
  readonly assetScopeKeys: readonly string[]
  readonly nodeId: string
}): void {
  if (input.assetScopeKeys.length === 0) {
    return
  }

  createImportedBodyOwnershipLookupKeys(input.key).forEach((bodyLookupKey) => {
    input.assetScopeKeys.forEach((assetScopeKey) => {
      setResolvedOwnerIndexValue(
        input.ownerNodeIdByBodyKey,
        createAssetScopedBodyOwnerKey(assetScopeKey, bodyLookupKey),
        input.nodeId,
      )
    })
  })
}

/**
 * 写入 body 归属索引，并为每个装配 occurrence scope 追加导出级精确 key。
 *
 * @param input 写入输入。
 */
function indexBodyOwnershipKey(input: {
  readonly ownerNodeIdByBodyKey: Map<string, PuppyBodyOwnerIndexValue>
  readonly key: string | undefined
  readonly nodeId: string
  readonly assetScopeKeys: readonly string[]
}): void {
  setOwnerIndexValue(input.ownerNodeIdByBodyKey, input.key, input.nodeId)
  setAssetScopedOwnerIndexValue({
    ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
    key: input.key,
    assetScopeKeys: input.assetScopeKeys,
    nodeId: input.nodeId,
  })
}

/**
 * 记录 hierarchy 节点覆盖的 body 归属。
 *
 * @param input 记录输入。
 */
function indexHierarchyBodyOwnership(input: {
  readonly node: GlbHierarchyNode
  readonly nodeId: string
  readonly ownerNodeIdByBodyKey: Map<string, PuppyBodyOwnerIndexValue>
}): void {
  input.node.meta?.bodyEntries?.forEach((entry) => {
    const assetScopeKeys = createHierarchyBodyAssetScopeLookupKeys({
      node: input.node,
      bodyBindingId: entry.bodyBindingId,
      bodyId: entry.bodyId,
      label: entry.label,
    })
    indexBodyOwnershipKey({
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      key: entry.bodyBindingId,
      nodeId: input.nodeId,
      assetScopeKeys,
    })
    indexBodyOwnershipKey({
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      key: entry.bodyId,
      nodeId: input.nodeId,
      assetScopeKeys,
    })
    indexBodyOwnershipKey({
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      key: entry.label,
      nodeId: input.nodeId,
      assetScopeKeys,
    })
  })

  Object.keys(input.node.meta?.bodyPrimitiveIndicesById ?? {}).forEach((bodyId) => {
    const assetScopeKeys = createHierarchyBodyAssetScopeLookupKeys({
      node: input.node,
      bodyId,
    })
    indexBodyOwnershipKey({
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      key: bodyId,
      nodeId: input.nodeId,
      assetScopeKeys,
    })
  })

  Object.keys(input.node.meta?.bodyHighlightTargetsById ?? {}).forEach((bodyId) => {
    const assetScopeKeys = createHierarchyBodyAssetScopeLookupKeys({
      node: input.node,
      bodyId,
    })
    indexBodyOwnershipKey({
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      key: bodyId,
      nodeId: input.nodeId,
      assetScopeKeys,
    })
  })
}

/**
 * 根据装配语义计算可承载 body 的零件节点边界。
 *
 * 设计约束：`.puppy` 的 display/body 归属必须落在可移动的零件实例上，assembly 只表达结构边界。
 * 这样即使历史 display mesh 把 bodyEntries 污染到装配根，也不会把后续拖拽目标解析到整棵装配。
 *
 * @param input 当前 hierarchy 遍历上下文。
 * @returns 当前子树可使用的 body owner；不存在时返回 null。
 */
function resolveNextBodyOwnerNodeId(input: {
  readonly currentNodeId: string | null
  readonly semanticKind: PuppyAssemblyNode['semanticKind'] | undefined
  readonly inheritedBodyOwnerNodeId: string | null
}): string | null {
  if (input.currentNodeId && input.semanticKind === 'part') {
    return input.currentNodeId
  }
  if (input.semanticKind === 'assembly') {
    return null
  }
  return input.inheritedBodyOwnerNodeId
}

/**
 * 规范化可选字符串。
 *
 * @param value 候选值。
 * @returns 非空字符串；无效时返回 undefined。
 */
function normalizeOptionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined
}

/**
 * 读取 assembly node 扩展字段中的选择代理源对象 ID。
 *
 * @param node assembly node。
 * @returns 选择代理源对象 ID。
 */
function readSelectionSourceObjectId(node: PuppyAssemblyNode): string | undefined {
  const extensions = node.extensions && typeof node.extensions === 'object'
    ? node.extensions as Record<string, unknown>
    : undefined
  return normalizeOptionalText(extensions?.selectionSourceObjectId)
}

/**
 * 创建可用于跨 fallback/semantic assembly 节点匹配的确定性身份 key。
 *
 * @param node assembly node。
 * @returns 身份 key 列表。
 */
function createAssemblyNodeIdentityKeys(node: PuppyAssemblyNode): string[] {
  const keys: string[] = []
  const sourceObjectId = normalizeOptionalText(node.sourceObjectId)
  const selectionSourceObjectId = readSelectionSourceObjectId(node)
  const metaSelectionObjectId = normalizeOptionalText(node.meta?.selectionObjectId)
  const metaAssemblyNodeId = normalizeOptionalText(node.meta?.assemblyNodeId)
  const externalFileUuid = normalizeOptionalText(node.meta?.externalFileUuid)
  const semanticKind = normalizeOptionalText(node.semanticKind ?? node.meta?.semanticKind)
  const path = normalizeOptionalText(node.path)

  if (sourceObjectId) {
    keys.push(`source-object:${sourceObjectId}`)
  }
  if (selectionSourceObjectId) {
    keys.push(`selection-source:${selectionSourceObjectId}`)
  }
  if (metaSelectionObjectId) {
    keys.push(`selection-source:${metaSelectionObjectId}`)
  }
  if (metaAssemblyNodeId) {
    keys.push(`assembly-node:${metaAssemblyNodeId}`)
  }
  if (externalFileUuid && semanticKind) {
    keys.push(`external-file:${semanticKind}:${externalFileUuid}`)
  }
  if (path && semanticKind) {
    keys.push(`semantic-path:${semanticKind}:${path}`)
  }

  return Array.from(new Set(keys))
}

/**
 * 写入唯一身份索引；重复 key 指向不同节点时标记为不可匹配。
 *
 * @param index 目标索引。
 * @param key 身份 key。
 * @param nodeId 节点 ID。
 */
function setUniqueNodeIdentityIndexValue(
  index: Map<string, PuppyAssemblyNodeIdentityIndexValue>,
  key: string,
  nodeId: string,
): void {
  const existing = index.get(key)
  if (existing === undefined) {
    index.set(key, nodeId)
    return
  }
  if (existing !== nodeId) {
    index.set(key, null)
  }
}

/**
 * 为目标语义 assembly 节点建立唯一身份索引。
 *
 * @param nodes 目标语义节点。
 * @returns 身份 key 到节点 ID 的索引。
 */
function createUniqueAssemblyNodeIdentityIndex(
  nodes: readonly PuppyAssemblyNode[],
): Map<string, PuppyAssemblyNodeIdentityIndexValue> {
  const index = new Map<string, PuppyAssemblyNodeIdentityIndexValue>()
  nodes.forEach((node) => {
    createAssemblyNodeIdentityKeys(node).forEach((key) => {
      setUniqueNodeIdentityIndexValue(index, key, node.nodeId)
    })
  })
  return index
}

/**
 * 解析 fallback 节点在目标语义 assembly 中的唯一匹配。
 *
 * @param node fallback assembly node。
 * @param identityIndex 目标语义节点身份索引。
 * @returns 唯一目标节点 ID；无法确定时返回 undefined。
 */
function resolveAssemblyNodeIdentityMatch(
  node: PuppyAssemblyNode,
  identityIndex: ReadonlyMap<string, PuppyAssemblyNodeIdentityIndexValue>,
): string | undefined {
  const candidates = createAssemblyNodeIdentityKeys(node)
    .map((key) => identityIndex.get(key))
    .filter((nodeId): nodeId is string => typeof nodeId === 'string' && nodeId.length > 0)
  const uniqueCandidates = Array.from(new Set(candidates))
  return uniqueCandidates.length === 1 ? uniqueCandidates[0] : undefined
}

/**
 * 追加节点 alias，避免无意义的自映射污染 Mate 文档。
 *
 * @param aliasByNodeId alias 映射。
 * @param sourceNodeId 源节点 ID。
 * @param targetNodeId 目标节点 ID。
 */
function setNodeAlias(
  aliasByNodeId: Record<string, string>,
  sourceNodeId: string | undefined,
  targetNodeId: string | undefined,
): void {
  if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) {
    return
  }
  const existingTargetNodeId = aliasByNodeId[sourceNodeId]
  if (existingTargetNodeId && existingTargetNodeId !== targetNodeId) {
    throw new Error(`PuppyCAD 离线包 Mate 节点引用存在冲突映射: ${sourceNodeId} -> ${existingTargetNodeId} / ${targetNodeId}`)
  }
  aliasByNodeId[sourceNodeId] = targetNodeId
}

/**
 * 创建 fallback assembly 节点 ID 到最终语义节点 ID 的 Mate 引用 alias。
 *
 * @param input alias 创建输入。
 * @returns 旧节点 ID 到最终节点 ID 的映射。
 */
function createMateNodeAliasByFallbackAssembly(input: {
  readonly fallbackAssembly: PuppyAssemblyDocument
  readonly foldedAssemblyNodes: readonly PuppyAssemblyNode[]
  readonly targetNodes: readonly PuppyAssemblyNode[]
  readonly targetRootNodeId: string
}): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  const identityIndex = createUniqueAssemblyNodeIdentityIndex(input.targetNodes)
  const targetNodeIdSet = new Set(input.targetNodes.map((node) => node.nodeId))

  /**
   * 文档根和 Mate 文档所属装配体必须直接指向最终 assembly.json 根节点。
   * 这是导出格式契约的一部分，不留给导入端二次猜测。
   */
  setNodeAlias(aliasByNodeId, input.fallbackAssembly.rootNodeId, input.targetRootNodeId)
  if (!targetNodeIdSet.has(input.fallbackAssembly.mates.assemblyId)) {
    setNodeAlias(aliasByNodeId, input.fallbackAssembly.mates.assemblyId, input.targetRootNodeId)
  }

  input.fallbackAssembly.nodes.forEach((node) => {
    const targetNodeId = resolveAssemblyNodeIdentityMatch(node, identityIndex)
    setNodeAlias(aliasByNodeId, node.nodeId, targetNodeId)
  })
  input.foldedAssemblyNodes.forEach((node) => {
    const targetNodeId = resolveAssemblyNodeIdentityMatch(node, identityIndex)
    setNodeAlias(aliasByNodeId, node.nodeId, targetNodeId)
  })

  return aliasByNodeId
}

/**
 * 收集最终语义节点在 fallback 与折叠节点中的全部运行时 Object3D 身份。
 *
 * hierarchy 节点只保留一个 `sourceObjectId`，但项目组合会把包内语义根迁空并折叠到项目
 * occurrence；此时选择代理仍可能指向被折叠对象 UUID。该索引只在当前导出事务中使用，
 * 让持久化最终化能够把所有运行时别名转换为同一个稳定 assemblyNodeId。
 *
 * @param input 运行时绑定收集输入。
 * @returns 最终装配节点到去重运行时 UUID 列表的绑定。
 */
function createRuntimeSourceObjectIdsByAssemblyNodeId(input: {
  readonly targetNodes: readonly PuppyAssemblyNode[]
  readonly fallbackAssembly: PuppyAssemblyDocument
  readonly foldedAssemblyNodes: readonly PuppyAssemblyNode[]
  readonly aliasByNodeId: Readonly<Record<string, string>>
}): Readonly<Record<string, readonly string[]>> {
  const validTargetNodeIds = new Set(input.targetNodes.map((node) => node.nodeId))
  const runtimeSourceObjectIdsByAssemblyNodeId = new Map<string, Set<string>>()
  const appendRuntimeSourceObjectId = (assemblyNodeId: string, sourceObjectId: unknown): void => {
    const normalizedSourceObjectId = normalizeOptionalText(sourceObjectId)
    if (!normalizedSourceObjectId || !validTargetNodeIds.has(assemblyNodeId)) {
      return
    }
    const sourceObjectIds = runtimeSourceObjectIdsByAssemblyNodeId.get(assemblyNodeId) ?? new Set<string>()
    sourceObjectIds.add(normalizedSourceObjectId)
    runtimeSourceObjectIdsByAssemblyNodeId.set(assemblyNodeId, sourceObjectIds)
  }

  input.targetNodes.forEach((node) => {
    appendRuntimeSourceObjectId(node.nodeId, node.sourceObjectId)
  })
  const sourceNodes = [
    ...input.fallbackAssembly.nodes,
    ...input.foldedAssemblyNodes,
  ]
  sourceNodes.forEach((sourceNode) => {
    const targetNodeId = resolvePuppyAssemblyNodeReferenceAlias(sourceNode.nodeId, input.aliasByNodeId)
    appendRuntimeSourceObjectId(targetNodeId, sourceNode.sourceObjectId)
  })

  return Object.fromEntries(
    input.targetNodes.map((node) => [
      node.nodeId,
      Array.from(runtimeSourceObjectIdsByAssemblyNodeId.get(node.nodeId) ?? []),
    ]),
  )
}

/**
 * 深拷贝装配节点本地矩阵。
 *
 * @param transform 源矩阵。
 * @returns 可安全写回目标节点的矩阵。
 */
function clonePuppyAssemblyNodeTransform(
  transform: PuppyAssemblyNode['transform'],
): Matrix4Tuple | undefined {
  return transform ? [...transform] as Matrix4Tuple : undefined
}

/**
 * 将 fallback 导出树中的本地矩阵按最终节点身份复制到权威 hierarchy 树。
 *
 * 设计说明：Mate 文档与 display 语义树都依赖同一批 assembly node ID。
 * 如果只重映射 Mate 引用、不重映射节点 transform，导入时配合组件状态会写到
 * 一个没有初始本地姿态的刚体根上，导致模型初始位置和正常打开工程不一致。
 *
 * @param input 矩阵复制输入。
 */
function applyFallbackAssemblyNodeTransforms(input: {
  readonly targetNodes: PuppyAssemblyNode[]
  readonly fallbackAssembly: PuppyAssemblyDocument
  readonly foldedAssemblyNodes: readonly PuppyAssemblyNode[]
  readonly aliasByNodeId: Readonly<Record<string, string>>
}): void {
  const targetNodeById = new Map(input.targetNodes.map((node) => [node.nodeId, node]))
  const sourceNodes = [
    ...input.fallbackAssembly.nodes,
    ...input.foldedAssemblyNodes,
  ]

  sourceNodes.forEach((sourceNode) => {
    const transform = clonePuppyAssemblyNodeTransform(sourceNode.transform)
    if (!transform) {
      return
    }
    const targetNodeId = resolvePuppyAssemblyNodeReferenceAlias(sourceNode.nodeId, input.aliasByNodeId)
    const targetNode = targetNodeById.get(targetNodeId)
    if (!targetNode || targetNode.transform) {
      return
    }
    targetNode.transform = transform
  })
}

/**
 * 判断未知值是否为普通对象记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/**
 * 克隆可写入 assembly.json 的扩展值，避免最终节点继续持有 fallback 节点的可变引用。
 *
 * @param value 扩展值。
 * @returns 克隆后的扩展值。
 */
function cloneAssemblyNodeExtensionValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(cloneAssemblyNodeExtensionValue)
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneAssemblyNodeExtensionValue(item)]),
    )
  }
  return value
}

/**
 * 按最终语义节点 ID 重写可选节点引用字段。
 *
 * @param value 扩展字段值。
 * @param aliasByNodeId fallback 节点 ID 到最终节点 ID 的映射。
 * @returns 重写后的字段值。
 */
function remapOptionalAssemblyNodeReference(
  value: unknown,
  aliasByNodeId: Readonly<Record<string, string>>,
): unknown {
  return typeof value === 'string' && value.trim().length > 0
    ? resolvePuppyAssemblyNodeReferenceAlias(value.trim(), aliasByNodeId)
    : value
}

/**
 * 重写引用复制扩展中的节点引用字段。
 *
 * 设计说明：
 * `referenceCopy` 会被导入端用于 materialize 轻量 occurrence，其中 `sourceNodeId` 等字段必须指向
 * 最终 assembly.json 节点，而不是被语义 hierarchy 替换掉的 fallback 节点。
 *
 * @param value 引用复制扩展值。
 * @param aliasByNodeId fallback 节点 ID 到最终节点 ID 的映射。
 * @returns 重写后的引用复制扩展值。
 */
function remapReferenceCopyExtensionValue(
  value: unknown,
  aliasByNodeId: Readonly<Record<string, string>>,
): unknown {
  if (!isRecord(value)) {
    return value
  }

  return {
    ...value,
    sourceNodeId: remapOptionalAssemblyNodeReference(value.sourceNodeId, aliasByNodeId),
    sourceComponentInstanceId: remapOptionalAssemblyNodeReference(value.sourceComponentInstanceId, aliasByNodeId),
    parentNodeId: remapOptionalAssemblyNodeReference(value.parentNodeId, aliasByNodeId),
    originOccurrenceId: remapOptionalAssemblyNodeReference(value.originOccurrenceId, aliasByNodeId),
    copiedFromOccurrenceId: remapOptionalAssemblyNodeReference(value.copiedFromOccurrenceId, aliasByNodeId),
  }
}

/**
 * 重写引用复制族扩展中的节点引用字段。
 *
 * @param value 引用复制族扩展值。
 * @param aliasByNodeId fallback 节点 ID 到最终节点 ID 的映射。
 * @returns 重写后的引用复制族扩展值。
 */
function remapReferenceLineageExtensionValue(
  value: unknown,
  aliasByNodeId: Readonly<Record<string, string>>,
): unknown {
  if (!isRecord(value)) {
    return value
  }

  return {
    ...value,
    originOccurrenceId: remapOptionalAssemblyNodeReference(value.originOccurrenceId, aliasByNodeId),
    copiedFromOccurrenceId: remapOptionalAssemblyNodeReference(value.copiedFromOccurrenceId, aliasByNodeId),
  }
}

/**
 * 克隆并重写 fallback 节点扩展字段。
 *
 * @param extensions fallback 节点扩展字段。
 * @param aliasByNodeId fallback 节点 ID 到最终节点 ID 的映射。
 * @returns 可合并到最终节点的扩展字段。
 */
function createRemappedFallbackAssemblyNodeExtensions(
  extensions: Record<string, unknown>,
  aliasByNodeId: Readonly<Record<string, string>>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(extensions).map(([key, value]) => {
      const clonedValue = cloneAssemblyNodeExtensionValue(value)
      if (key === 'referenceCopy') {
        return [key, remapReferenceCopyExtensionValue(clonedValue, aliasByNodeId)]
      }
      if (key === 'referenceLineage') {
        return [key, remapReferenceLineageExtensionValue(clonedValue, aliasByNodeId)]
      }
      return [key, clonedValue]
    }),
  )
}

/**
 * 将 fallback 导出树中的节点扩展字段按最终节点身份迁移到权威 hierarchy 树。
 *
 * 设计说明：
 * 语义 hierarchy 是 `assembly.json` 的权威来源，但引用复制、选择代理等运行时扩展来自实际 Object3D。
 * 因此扩展字段必须和 transform 一样通过 alias 迁移，否则导入端会丢失轻量 occurrence 的 materialize 信息。
 *
 * @param input 扩展迁移输入。
 */
function applyFallbackAssemblyNodeExtensions(input: {
  readonly targetNodes: PuppyAssemblyNode[]
  readonly fallbackAssembly: PuppyAssemblyDocument
  readonly foldedAssemblyNodes: readonly PuppyAssemblyNode[]
  readonly aliasByNodeId: Readonly<Record<string, string>>
}): void {
  const targetNodeById = new Map(input.targetNodes.map((node) => [node.nodeId, node]))
  const sourceNodes = [
    ...input.fallbackAssembly.nodes,
    ...input.foldedAssemblyNodes,
  ]

  sourceNodes.forEach((sourceNode) => {
    if (!sourceNode.extensions) {
      return
    }

    const targetNodeId = resolvePuppyAssemblyNodeReferenceAlias(sourceNode.nodeId, input.aliasByNodeId)
    const targetNode = targetNodeById.get(targetNodeId)
    if (!targetNode) {
      return
    }

    const remappedExtensions = createRemappedFallbackAssemblyNodeExtensions(
      sourceNode.extensions,
      input.aliasByNodeId,
    )
    const nextExtensions = {
      ...remappedExtensions,
      ...(targetNode.extensions ?? {}),
    }
    targetNode.extensions = Object.keys(nextExtensions).length > 0
      ? nextExtensions
      : undefined
  })
}

/**
 * 收集一个可选字符串节点引用。
 *
 * @param nodeIds 节点引用集合。
 * @param value 待收集的值。
 */
function appendMateReferenceNodeId(nodeIds: Set<string>, value: unknown): void {
  if (typeof value !== 'string') {
    return
  }
  const nodeId = value.trim()
  if (nodeId.length > 0) {
    nodeIds.add(nodeId)
  }
}

/**
 * 收集 Mate 实体中用于定位装配节点的引用字段。
 *
 * @param entity Mate 实体引用。
 * @returns 实体内出现过的装配节点 ID。
 */
function collectMateEntityReferenceNodeIds(entity: unknown): readonly string[] {
  if (!isRecord(entity)) {
    return []
  }

  const nodeIds = new Set<string>()
  const topologyRef = isRecord(entity.topologyRef) ? entity.topologyRef : null
  const referenceDependency = isRecord(entity.referenceDependency)
    ? entity.referenceDependency
    : null
  const directValues = [
    entity.assemblyNodeId,
    entity.componentInstanceId,
    topologyRef?.assemblyNodeId,
  ]
  directValues.forEach((value) => appendMateReferenceNodeId(nodeIds, value))

  const driverComponentInstanceIds = Array.isArray(referenceDependency?.driverComponentInstanceIds)
    ? referenceDependency.driverComponentInstanceIds
    : []
  driverComponentInstanceIds.forEach((value) => appendMateReferenceNodeId(nodeIds, value))

  const driverTopologyRefs = Array.isArray(referenceDependency?.driverTopologyRefs)
    ? referenceDependency.driverTopologyRefs
    : []
  driverTopologyRefs.forEach((value) => {
    if (isRecord(value)) {
      appendMateReferenceNodeId(nodeIds, value.assemblyNodeId)
    }
  })

  return Array.from(nodeIds)
}

/**
 * 收集 Mate 文档中真实出现过的装配节点引用。
 *
 * @param document Mate 文档。
 * @returns Mate 文档引用过的节点 ID 列表。
 */
function collectMateDocumentReferenceNodeIds(
  document: PuppyAssemblyDocument['mates'],
): readonly string[] {
  const nodeIds = new Set<string>()
  appendMateReferenceNodeId(nodeIds, document.assemblyId)

  if (isRecord(document.components)) {
    Object.keys(document.components).forEach((componentInstanceId) => {
      appendMateReferenceNodeId(nodeIds, componentInstanceId)
    })
    Object.values(document.components).forEach((component) => {
      if (isRecord(component)) {
        appendMateReferenceNodeId(nodeIds, component.componentInstanceId)
      }
    })
  }

  document.mates.forEach((mate) => {
    appendMateReferenceNodeId(nodeIds, mate.assemblyId)
    collectMateEntityReferenceNodeIds(mate.firstEntity)
      .forEach((nodeId) => appendMateReferenceNodeId(nodeIds, nodeId))
    collectMateEntityReferenceNodeIds(mate.secondEntity)
      .forEach((nodeId) => appendMateReferenceNodeId(nodeIds, nodeId))
    collectMateEntityReferenceNodeIds(mate.referenceEntity)
      .forEach((nodeId) => appendMateReferenceNodeId(nodeIds, nodeId))
  })

  return Array.from(nodeIds)
}

/**
 * 尝试把带有 scoped 内嵌包根的节点 ID 折叠到 Mate 已经引用的权威 ID。
 *
 * 只有不对应真实 occurrence 的中间路径段才属于历史包作用域噪声。末级 segment
 * 表达当前节点自身，任何情况下都不能依据 Mate owner 删除；中间路径前缀已经出现在
 * hierarchy occurrence 集合中时同样不能删除，否则会破坏真实装配父子结构。
 *
 * @param input 折叠匹配输入。
 * @returns 唯一命中的权威节点 ID；无法确定时返回 undefined。
 */
function resolveScopedEmbeddedCanonicalNodeId(input: {
  readonly sourceNodeId: string
  readonly referenceNodeIds: readonly string[]
  readonly occurrenceNodeIds: ReadonlySet<string>
}): string | undefined {
  const sourceSegments = splitAssemblyNodePath(input.sourceNodeId)
  const sourceCanonicalNodeId = sourceSegments.join('/')
  if (sourceSegments.length < 2 || sourceCanonicalNodeId.length === 0) {
    return undefined
  }

  const sourcePrefixNodeIds = sourceSegments.map((_, index) => (
    sourceSegments.slice(0, index + 1).join('/')
  ))
  const matchedReferenceNodeIds = new Set<string>()
  input.referenceNodeIds.forEach((referenceNodeId) => {
    const referenceSegments = splitAssemblyNodePath(referenceNodeId)
    const referenceCanonicalNodeId = referenceSegments.join('/')
    const deleteCount = sourceSegments.length - referenceSegments.length
    if (deleteCount <= 0 || referenceCanonicalNodeId.length === 0) {
      return
    }

    for (let startIndex = 1; startIndex + deleteCount <= sourceSegments.length; startIndex += 1) {
      const deletesLeafSegment = startIndex + deleteCount === sourceSegments.length
      if (deletesLeafSegment) {
        continue
      }

      let deletesOccurrencePath = false
      for (
        let deletedSegmentIndex = startIndex;
        deletedSegmentIndex < startIndex + deleteCount;
        deletedSegmentIndex += 1
      ) {
        const deletedPathPrefix = sourcePrefixNodeIds[deletedSegmentIndex]
        if (deletedPathPrefix && input.occurrenceNodeIds.has(deletedPathPrefix)) {
          deletesOccurrencePath = true
          break
        }
      }
      if (deletesOccurrencePath) {
        continue
      }

      const candidateNodeId = [
        ...sourceSegments.slice(0, startIndex),
        ...sourceSegments.slice(startIndex + deleteCount),
      ].join('/')
      if (candidateNodeId === referenceCanonicalNodeId) {
        matchedReferenceNodeIds.add(referenceNodeId)
      }
    }
  })

  return matchedReferenceNodeIds.size === 1
    ? [...matchedReferenceNodeIds][0]
    : undefined
}

/**
 * 创建 hierarchy 节点到 Mate 权威节点的 scoped 折叠别名。
 *
 * @param input 别名创建输入。
 * @returns scoped 内嵌根节点 ID 到最终节点 ID 的映射。
 */
function createScopedEmbeddedAssemblyNodeAliasByMateReferences(input: {
  readonly nodes: readonly PuppyAssemblyNode[]
  readonly mateReferenceNodeIds: readonly string[]
}): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  const referenceNodeIdSet = new Set(input.mateReferenceNodeIds)
  const occurrenceNodeIds = new Set<string>()
  input.nodes.forEach((node) => {
    [node.nodeId, node.meta?.assemblyNodeId].forEach((nodeId) => {
      if (!nodeId) {
        return
      }
      const canonicalNodeId = splitAssemblyNodePath(nodeId).join('/')
      if (canonicalNodeId) {
        occurrenceNodeIds.add(canonicalNodeId)
      }
    })
  })

  input.nodes.forEach((node) => {
    const canonicalNodeId = referenceNodeIdSet.has(node.nodeId)
      ? undefined
      : resolveScopedEmbeddedCanonicalNodeId({
        sourceNodeId: node.nodeId,
        referenceNodeIds: input.mateReferenceNodeIds,
        occurrenceNodeIds,
      })
    setNodeAlias(aliasByNodeId, node.nodeId, canonicalNodeId)

    if (node.meta?.assemblyNodeId && node.meta.assemblyNodeId !== node.nodeId) {
      const canonicalMetaNodeId = referenceNodeIdSet.has(node.meta.assemblyNodeId)
        ? undefined
        : resolveScopedEmbeddedCanonicalNodeId({
          sourceNodeId: node.meta.assemblyNodeId,
          referenceNodeIds: input.mateReferenceNodeIds,
          occurrenceNodeIds,
        })
      setNodeAlias(aliasByNodeId, node.meta.assemblyNodeId, canonicalMetaNodeId)
    }
  })

  return aliasByNodeId
}

/**
 * 按节点别名重写可选装配节点 ID。
 *
 * @param nodeId 原节点 ID。
 * @param aliasByNodeId 节点别名映射。
 * @returns 最终节点 ID。
 */
function remapNullableAssemblyNodeId(
  nodeId: string | null | undefined,
  aliasByNodeId: Readonly<Record<string, string>>,
): string | null {
  return nodeId
    ? resolvePuppyAssemblyNodeReferenceAlias(nodeId, aliasByNodeId)
    : null
}

/**
 * 克隆并重写 assembly node 的稳定身份。
 *
 * @param node 原 assembly node。
 * @param aliasByNodeId 节点别名映射。
 * @returns 使用最终节点 ID 的 assembly node。
 */
function cloneAssemblyNodeWithCanonicalId(
  node: PuppyAssemblyNode,
  aliasByNodeId: Readonly<Record<string, string>>,
): PuppyAssemblyNode {
  const nodeId = resolvePuppyAssemblyNodeReferenceAlias(node.nodeId, aliasByNodeId)
  const parentNodeId = remapNullableAssemblyNodeId(node.parentNodeId, aliasByNodeId)
  const safeParentNodeId = parentNodeId === nodeId ? null : parentNodeId
  return {
    ...node,
    nodeId,
    parentNodeId: safeParentNodeId,
    transform: clonePuppyAssemblyNodeTransform(node.transform),
    meta: node.meta
      ? {
        ...node.meta,
        assemblyNodeId: nodeId,
      }
      : undefined,
    displayNodeIds: [...node.displayNodeIds],
    bodyBindingIds: [...node.bodyBindingIds],
    geometryObjectIds: [...node.geometryObjectIds],
    extensions: node.extensions ? { ...node.extensions } : undefined,
  }
}

/**
 * 合并折叠后指向同一节点的 display/body 绑定信息。
 *
 * @param target 保留的目标节点。
 * @param source 被折叠的源节点。
 */
function mergeCanonicalizedAssemblyNode(
  target: PuppyAssemblyNode,
  source: PuppyAssemblyNode,
): void {
  appendUniqueStrings(target.displayNodeIds, source.displayNodeIds)
  appendUniqueStrings(target.bodyBindingIds, source.bodyBindingIds)
  appendUniqueStrings(target.geometryObjectIds, source.geometryObjectIds)
  if (!target.transform && source.transform) {
    target.transform = clonePuppyAssemblyNodeTransform(source.transform)
  }
  if (!target.extensions && source.extensions) {
    target.extensions = { ...source.extensions }
    return
  }
  if (target.extensions && source.extensions) {
    target.extensions = {
      ...source.extensions,
      ...target.extensions,
    }
  }
}

/**
 * 按节点别名同步 body 归属索引。
 *
 * @param ownerNodeIdByBodyKey 原 body 归属索引。
 * @param aliasByNodeId 节点别名映射。
 * @returns 使用最终节点 ID 的 body 归属索引。
 */
function remapOwnerNodeIdByBodyKey(
  ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>,
  aliasByNodeId: Readonly<Record<string, string>>,
): ReadonlyMap<string, PuppyBodyOwnerIndexValue> {
  return new Map(Array.from(ownerNodeIdByBodyKey.entries()).map(([key, ownerNodeId]) => [
    key,
    typeof ownerNodeId === 'string'
      ? resolvePuppyAssemblyNodeReferenceAlias(ownerNodeId, aliasByNodeId)
      : ownerNodeId,
  ]))
}

/**
 * 按节点父级字段重建 childIdsByNodeId。
 *
 * @param nodes 最终节点列表。
 * @param rootNodeId 根节点 ID。
 * @returns 父节点到子节点的映射。
 */
function createChildIdsByCanonicalNodeId(
  nodes: readonly PuppyAssemblyNode[],
  rootNodeId: string,
): Record<string, string[]> {
  const nodeById = new Map(nodes.map((node) => [node.nodeId, node]))
  const childIdsByNodeId: Record<string, string[]> = {}
  nodes.forEach((node) => {
    childIdsByNodeId[node.nodeId] = []
  })

  nodes.forEach((node) => {
    if (!node.parentNodeId) {
      return
    }
    if (!nodeById.has(node.parentNodeId)) {
      throw new Error(`PuppyCAD 离线包装配节点父级折叠后不存在: ${node.nodeId}/${node.parentNodeId}`)
    }
    if (node.nodeId !== rootNodeId) {
      appendUniqueStrings(childIdsByNodeId[node.parentNodeId]!, [node.nodeId])
    }
  })

  return childIdsByNodeId
}

/**
 * 将 hierarchy 生成的装配图按 Mate 引用折叠到同一套节点 ID。
 *
 * @param input 折叠输入。
 * @returns 已归一化的装配图和别名表。
 */
function canonicalizeHierarchyAssemblyGraphByMateReferences(input: {
  readonly rootNodeId: string
  readonly nodes: readonly PuppyAssemblyNode[]
  readonly ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>
  readonly mates: PuppyAssemblyDocument['mates']
}): PuppyCanonicalizedHierarchyAssemblyGraph {
  const aliasByNodeId = createScopedEmbeddedAssemblyNodeAliasByMateReferences({
    nodes: input.nodes,
    mateReferenceNodeIds: collectMateDocumentReferenceNodeIds(input.mates),
  })
  if (Object.keys(aliasByNodeId).length === 0) {
    return {
      rootNodeId: input.rootNodeId,
      nodes: [...input.nodes],
      childIdsByNodeId: createChildIdsByCanonicalNodeId(input.nodes, input.rootNodeId),
      ownerNodeIdByBodyKey: input.ownerNodeIdByBodyKey,
      aliasByNodeId,
    }
  }

  const nodes: PuppyAssemblyNode[] = []
  const nodeById = new Map<string, PuppyAssemblyNode>()
  input.nodes.forEach((node) => {
    const canonicalNode = cloneAssemblyNodeWithCanonicalId(node, aliasByNodeId)
    const existingNode = nodeById.get(canonicalNode.nodeId)
    if (existingNode) {
      mergeCanonicalizedAssemblyNode(existingNode, canonicalNode)
      return
    }
    nodeById.set(canonicalNode.nodeId, canonicalNode)
    nodes.push(canonicalNode)
  })

  const rootNodeId = resolvePuppyAssemblyNodeReferenceAlias(input.rootNodeId, aliasByNodeId)
  if (!nodeById.has(rootNodeId)) {
    throw new Error(`PuppyCAD 离线包装配根节点折叠后不存在: ${rootNodeId}`)
  }

  return {
    rootNodeId,
    nodes,
    childIdsByNodeId: createChildIdsByCanonicalNodeId(nodes, rootNodeId),
    ownerNodeIdByBodyKey: remapOwnerNodeIdByBodyKey(input.ownerNodeIdByBodyKey, aliasByNodeId),
    aliasByNodeId,
  }
}

/**
 * 合并多套节点别名，并保留冲突检测。
 *
 * @param aliasMaps 待合并别名表。
 * @returns 合并后的别名表。
 */
function mergeNodeAliasMaps(
  ...aliasMaps: readonly Readonly<Record<string, string>>[]
): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  aliasMaps.forEach((aliasMap) => {
    Object.entries(aliasMap).forEach(([sourceNodeId, targetNodeId]) => {
      setNodeAlias(aliasByNodeId, sourceNodeId, targetNodeId)
    })
  })
  return aliasByNodeId
}

/**
 * 从 hierarchy 构建 assembly.json。
 *
 * @param hierarchy 权威 hierarchy。
 * @param fallbackAssembly 原导出链路生成的兜底 assembly，用于复用基础导出结果。
 * @returns assembly.json 与 body 归属索引。
 */
function buildAssemblyDocumentFromHierarchy(
  hierarchy: GlbHierarchyNode,
  fallbackAssembly: PuppyAssemblyDocument,
  foldedAssemblyNodes: readonly PuppyAssemblyNode[],
  sourceObjectLocalTransforms: Readonly<Record<string, Matrix4Tuple>>,
): PuppyHierarchyAssemblyBuildResult | null {
  const nodes: PuppyAssemblyNode[] = []
  const childIdsByNodeId: Record<string, string[]> = {}
  const ownerNodeIdByBodyKey = new Map<string, PuppyBodyOwnerIndexValue>()
  const usedNodeIds = new Set<string>()

  const visit = (
    node: GlbHierarchyNode,
    parentNodeId: string | null,
    isRoot: boolean,
    bodyOwnerNodeId: string | null,
  ): string[] => {
    const semanticKind = resolveExportableSemanticKind(node, isRoot)
    /**
     * 零件 occurrence 是不可再拆分的刚体边界。Finalizer 会让其内部显示层、Mesh 与虚拟 Body
     * 共享零件根 occurrence；这里必须消费该语义，不能再按 hierarchy 行生成 `#2` 节点。
     * 真正的子组件只会出现在 assembly 边界下，因此进入 part 后所有后代仅贡献显示/body 绑定。
     */
    const isInsidePartOccurrence = bodyOwnerNodeId !== null
    const currentNodeId = semanticKind && !isInsidePartOccurrence
      ? createAssemblyNodeId(node, usedNodeIds)
      : null
    const nextBodyOwnerNodeId = isInsidePartOccurrence
      ? bodyOwnerNodeId
      : resolveNextBodyOwnerNodeId({
          currentNodeId,
          semanticKind,
          inheritedBodyOwnerNodeId: bodyOwnerNodeId,
        })
    if (currentNodeId && semanticKind) {
      const assemblyNode = createAssemblyNodeFromHierarchy({
        node,
        nodeId: currentNodeId,
        parentNodeId,
        semanticKind,
        sourceObjectLocalTransforms,
      })
      nodes.push(assemblyNode)
      childIdsByNodeId[currentNodeId] = []
      if (semanticKind === 'part') {
        indexHierarchyBodyOwnership({
          node,
          nodeId: currentNodeId,
          ownerNodeIdByBodyKey,
        })
      }
    } else if (nextBodyOwnerNodeId) {
      /**
       * 非导出的实体/包装节点仍可能携带 bodyEntries。它们不是 assembly.json 节点，
       * 但对应几何必须归属到最近的零件节点，否则显示绑定会退回根节点。
       */
      indexHierarchyBodyOwnership({
        node,
        nodeId: nextBodyOwnerNodeId,
        ownerNodeIdByBodyKey,
      })
    }

    const childParentNodeId = currentNodeId ?? parentNodeId
    const retainedChildNodeIds = node.children.flatMap((child) => (
      visit(child, childParentNodeId, false, nextBodyOwnerNodeId)
    ))

    if (currentNodeId) {
      childIdsByNodeId[currentNodeId] = retainedChildNodeIds
      return [currentNodeId]
    }
    return retainedChildNodeIds
  }

  const retainedRootNodeIds = visit(hierarchy, null, true, null)
  const rootNodeId = retainedRootNodeIds[0]
  if (!rootNodeId || nodes.length === 0) {
    return null
  }
  const canonicalAssemblyGraph = canonicalizeHierarchyAssemblyGraphByMateReferences({
    rootNodeId,
    nodes,
    ownerNodeIdByBodyKey,
    mates: fallbackAssembly.mates,
  })
  const mateNodeAliasByFallbackNodeId = createMateNodeAliasByFallbackAssembly({
    fallbackAssembly,
    foldedAssemblyNodes,
    targetNodes: canonicalAssemblyGraph.nodes,
    targetRootNodeId: canonicalAssemblyGraph.rootNodeId,
  })
  const aliasByNodeId = mergeNodeAliasMaps(
    canonicalAssemblyGraph.aliasByNodeId,
    mateNodeAliasByFallbackNodeId,
  )
  const runtimeSourceObjectIdsByAssemblyNodeId = createRuntimeSourceObjectIdsByAssemblyNodeId({
    targetNodes: canonicalAssemblyGraph.nodes,
    fallbackAssembly,
    foldedAssemblyNodes,
    aliasByNodeId,
  })
  applyFallbackAssemblyNodeTransforms({
    targetNodes: canonicalAssemblyGraph.nodes,
    fallbackAssembly,
    foldedAssemblyNodes,
    aliasByNodeId,
  })
  applyFallbackAssemblyNodeExtensions({
    targetNodes: canonicalAssemblyGraph.nodes,
    fallbackAssembly,
    foldedAssemblyNodes,
    aliasByNodeId,
  })
  const fallbackDatumExtension = readPuppyDatumExtensionFromAssemblyExtensions(fallbackAssembly.extensions)
  const remappedDatumExtension = fallbackDatumExtension
    ? remapPuppyDatumExtensionNodeReferences({
      datumExtension: fallbackDatumExtension,
      aliasByNodeId,
    })
    : undefined
  const remappedExtensions = writePuppyDatumExtensionToAssemblyExtensions(
    fallbackAssembly.extensions,
    remappedDatumExtension,
  )

  /**
   * hierarchy 根被保留时 retainedRootNodeIds 只会有一个根。
   * 如果调用方传入的根是非语义包装层，visit 会把其语义子节点提升上来；
   * 此时仍然用第一个语义节点作为文档根，保持导入链路简单稳定。
   */
  return {
    assembly: {
      version: PUPPY_PACKAGE_VERSION,
      rootNodeId: canonicalAssemblyGraph.rootNodeId,
      nodes: canonicalAssemblyGraph.nodes,
      childIdsByNodeId: canonicalAssemblyGraph.childIdsByNodeId,
      mates: remapPuppyAssemblyMateDocumentNodeReferences({
        mates: fallbackAssembly.mates,
        aliasByNodeId,
      }),
      extensions: remappedExtensions,
    },
    ownerNodeIdByBodyKey: canonicalAssemblyGraph.ownerNodeIdByBodyKey,
    runtimeSourceObjectIdsByAssemblyNodeId,
  }
}

/**
 * 向数组追加去重字符串。
 *
 * @param target 目标数组。
 * @param values 待追加字符串。
 */
function appendUniqueStrings(target: string[], values: readonly string[]): void {
  values.forEach((value) => {
    if (value.trim().length > 0 && !target.includes(value)) {
      target.push(value)
    }
  })
}

/**
 * 从 display 的 body 绑定中读取 BRep 资产作用域。
 *
 * @param bodyBinding display body 绑定。
 * @returns 可用于精确 owner 匹配的资产作用域 key。
 */
function createDisplayBodyBindingAssetScopeLookupKeys(
  bodyBinding: PuppyDisplayNodeBinding['bodyBindings'][number],
): string[] {
  return Array.from(new Set([
    ...extractImportedBrepAssetScopeLookupKeys(bodyBinding.geometryObjectId),
    ...extractImportedBrepAssetScopeLookupKeys(bodyBinding.bodyBindingId),
    ...extractImportedBrepAssetScopeLookupKeys(bodyBinding.bodyId),
    ...extractImportedBrepAssetScopeLookupKeys(bodyBinding.name),
  ]))
}

/**
 * 按 BRep 资产作用域解析 display body 的精确 owner。
 *
 * @param input 解析输入。
 * @returns 命中的 owner node ID。
 */
function resolveAssetScopedBindingOwnerCandidates(input: {
  readonly bodyBinding: PuppyDisplayNodeBinding['bodyBindings'][number]
  readonly ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>
}): string[] {
  const assetScopeKeys = createDisplayBodyBindingAssetScopeLookupKeys(input.bodyBinding)
  if (assetScopeKeys.length === 0) {
    return []
  }

  const candidates: string[] = []
  const bodyIdentityKeys = [
    input.bodyBinding.bodyId,
    input.bodyBinding.bodyBindingId,
    input.bodyBinding.name,
  ]
  bodyIdentityKeys.forEach((bodyIdentityKey) => {
    createImportedBodyOwnershipLookupKeys(bodyIdentityKey).forEach((bodyLookupKey) => {
      assetScopeKeys.forEach((assetScopeKey) => {
        const ownerNodeId = input.ownerNodeIdByBodyKey.get(
          createAssetScopedBodyOwnerKey(assetScopeKey, bodyLookupKey),
        )
        if (typeof ownerNodeId === 'string' && ownerNodeId.length > 0) {
          candidates.push(ownerNodeId)
        }
      })
    })
  })

  return Array.from(new Set(candidates))
}

/**
 * 从 body 绑定解析候选 owner node ID。
 *
 * @param binding display node 绑定。
 * @param ownerNodeIdByBodyKey body 归属索引。
 * @returns 命中的 owner node ID 列表。
 */
function resolveBindingOwnerCandidates(
  binding: PuppyDisplayNodeBinding,
  ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>,
): string[] {
  const candidates: string[] = []
  binding.bodyBindings.forEach((bodyBinding) => {
    candidates.push(...resolveAssetScopedBindingOwnerCandidates({
      bodyBinding,
      ownerNodeIdByBodyKey,
    }))
    /**
     * body 归属只能来自 createImportedBodyOwnershipLookupKeys 生成的严格 key。
     * 这里不能做 substring 兜底，避免 `#body:1` 误命中 `#body:10` 后把 display 错挂到共同祖先。
     */
    const bodyIdentityKeys = [
      bodyBinding.bodyId,
      bodyBinding.bodyBindingId,
      bodyBinding.geometryObjectId,
      bodyBinding.name,
    ]
    bodyIdentityKeys.forEach((key) => {
      createImportedBodyOwnershipLookupKeys(key).forEach((lookupKey) => {
        const ownerNodeId = ownerNodeIdByBodyKey.get(lookupKey)
        if (typeof ownerNodeId === 'string' && ownerNodeId.length > 0) {
          candidates.push(ownerNodeId)
        }
      })
    })
  })

  return Array.from(new Set(candidates))
}

/**
 * 从 display body 绑定读取已经闭合的 occurrence 归属。
 *
 * 切换后的零件与其它实例可以复用同一 BRep 定义，因此 bodyId、geometryObjectId 和文件作用域
 * 都不足以区分组件。bodyBindingId 中已经存在且仍属于当前 assembly 图的 occurrence 才是
 * 最终实例身份；同一 display 同时声明多个 occurrence 属于协议冲突，必须阻止继续导出。
 *
 * @param binding 当前 display 绑定。
 * @param nodeById 当前 assembly 节点索引。
 * @returns 唯一有效的 occurrence owner；未声明或已失效时返回 undefined。
 */
function resolveBindingOccurrenceOwnerNodeId(
  binding: PuppyDisplayNodeBinding,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
): string | undefined {
  const occurrenceNodeIds = Array.from(new Set(binding.bodyBindings
    .map((bodyBinding) => resolveImportedBodyOccurrenceNodeId(bodyBinding.bodyBindingId))
    .filter((nodeId) => nodeId.length > 0)))
  if (occurrenceNodeIds.length > 1) {
    throw new Error(
      `PuppyCAD display 节点包含多个 body occurrence: ${binding.displayNodeId}/${occurrenceNodeIds.join(',')}`,
    )
  }

  const occurrenceNodeId = occurrenceNodeIds[0]
  return occurrenceNodeId && nodeById.has(occurrenceNodeId)
    ? occurrenceNodeId
    : undefined
}

/**
 * 建立 assembly node 父节点索引。
 *
 * @param assembly assembly.json。
 * @returns node ID 到父 node ID 的索引。
 */
function createParentNodeIdByNodeId(assembly: PuppyAssemblyDocument): Map<string, string | null> {
  return new Map(assembly.nodes.map((node) => [node.nodeId, node.parentNodeId]))
}

/**
 * 计算节点到根的祖先链，链首为当前节点。
 *
 * @param nodeId 当前节点 ID。
 * @param parentNodeIdByNodeId 父节点索引。
 * @returns 祖先链。
 */
function createAncestorChain(
  nodeId: string,
  parentNodeIdByNodeId: ReadonlyMap<string, string | null>,
): string[] {
  const chain: string[] = []
  const visited = new Set<string>()
  let current: string | null | undefined = nodeId
  while (current && !visited.has(current)) {
    visited.add(current)
    chain.push(current)
    current = parentNodeIdByNodeId.get(current) ?? null
  }
  return chain
}

/**
 * 为多个候选 owner 选择最近公共祖先。
 *
 * @param input 选择输入。
 * @returns 最适合承载 display 节点的 assembly node ID。
 */
function resolveCommonOwnerNodeId(input: {
  readonly nodeIds: readonly string[]
  readonly assembly: PuppyAssemblyDocument
}): string {
  const uniqueNodeIds = Array.from(new Set(input.nodeIds))
  if (uniqueNodeIds.length === 0) {
    return input.assembly.rootNodeId
  }
  if (uniqueNodeIds.length === 1) {
    return uniqueNodeIds[0]!
  }

  const parentNodeIdByNodeId = createParentNodeIdByNodeId(input.assembly)
  const chains = uniqueNodeIds.map((nodeId) => createAncestorChain(nodeId, parentNodeIdByNodeId))
  const firstChain = chains[0] ?? []
  return firstChain.find((candidate) => chains.every((chain) => chain.includes(candidate)))
    ?? input.assembly.rootNodeId
}

/**
 * 解析 display node 的最终归属。
 *
 * @param input 解析输入。
 * @returns display 归属。
 */
function resolveDisplayOwnerships(input: {
  readonly assembly: PuppyAssemblyDocument
  readonly nodeBindings: readonly PuppyDisplayNodeBinding[]
  readonly ownerNodeIdByBodyKey: ReadonlyMap<string, PuppyBodyOwnerIndexValue>
}): PuppyDisplayOwnership[] {
  const nodeById = new Map(input.assembly.nodes.map((node) => [node.nodeId, node]))
  return input.nodeBindings.map((binding) => {
    const occurrenceOwnerNodeId = resolveBindingOccurrenceOwnerNodeId(binding, nodeById)
    if (occurrenceOwnerNodeId) {
      return {
        displayNodeId: binding.displayNodeId,
        ownerNodeId: occurrenceOwnerNodeId,
      }
    }

    const ownerCandidates = resolveBindingOwnerCandidates(binding, input.ownerNodeIdByBodyKey)
    if (ownerCandidates.length > 0) {
      return {
        displayNodeId: binding.displayNodeId,
        ownerNodeId: resolveCommonOwnerNodeId({
          nodeIds: ownerCandidates,
          assembly: input.assembly,
        }),
      }
    }

    if (binding.assemblyNodeId && nodeById.has(binding.assemblyNodeId)) {
      return {
        displayNodeId: binding.displayNodeId,
        ownerNodeId: binding.assemblyNodeId,
      }
    }

    return {
      displayNodeId: binding.displayNodeId,
      ownerNodeId: resolveCommonOwnerNodeId({
        nodeIds: ownerCandidates,
        assembly: input.assembly,
      }),
    }
  })
}

/**
 * 将 body 绑定重写为权威装配 occurrence 级身份。
 *
 * 设计说明：display 归属解析可以使用源 body 定义、asset scope 等宽松候选；
 * 但 `.puppy` 落盘后的交互身份必须唯一指向组件 occurrence，否则刷新后移动命令会重新合并同源 body。
 *
 * @param bodyBinding display body 绑定。
 * @param ownerNodeId 当前 display 最终归属的 assembly node ID。
 * @returns occurrence 级 body 绑定。
 */
function cloneBodyBindingForDisplayOwner(
  bodyBinding: PuppyDisplayNodeBodyBinding,
  ownerNodeId: string,
): PuppyDisplayNodeBodyBinding {
  const bodyBindingId = createImportedBodyOccurrenceBindingId(bodyBinding.bodyBindingId, ownerNodeId)
  return bodyBindingId === bodyBinding.bodyBindingId
    ? bodyBinding
    : {
        ...bodyBinding,
        bodyBindingId,
      }
}

/**
 * 将 body 拓扑资产重写为与 display body 绑定一致的身份。
 *
 * @param asset 原始 body 拓扑资产。
 * @param bodyBinding 已完成 occurrence 重写的 body 绑定。
 * @returns 可写入 topology-runtime 的拓扑资产。
 */
function cloneBodyTopologyAssetForDisplayOwner(input: {
  readonly asset: PuppyVisibleBodyTopologyAsset
  readonly bodyBinding: PuppyDisplayNodeBodyBinding
}): PuppyVisibleBodyTopologyAsset {
  return {
    ...input.asset,
    bodyBindingId: input.bodyBinding.bodyBindingId,
    geometryObjectId: input.bodyBinding.geometryObjectId,
    meshData: {
      ...input.asset.meshData,
      metadata: {
        ...(input.asset.meshData.metadata ?? {}),
        objectId: input.bodyBinding.geometryObjectId,
        bodyBindingId: input.bodyBinding.bodyBindingId,
        bodyId: input.bodyBinding.bodyId,
      },
    },
  }
}

/**
 * 将解析出的 display owner 写回持久化绑定与 topology-runtime 资产。
 *
 * @param input display 绑定、body 拓扑资产与 owner 解析结果。
 * @returns 带最终 assemblyNodeId、occurrence 级 bodyBindingId 与同步拓扑资产的绑定快照。
 */
function applyDisplayOwnershipsToExportBindings(input: {
  readonly nodeBindings: readonly PuppyDisplayNodeBinding[]
  readonly bodyTopologyAssets: readonly PuppyVisibleBodyTopologyAsset[]
  readonly ownerships: readonly PuppyDisplayOwnership[]
}): PuppyDisplayOwnershipBindingResult {
  const ownerNodeIdByDisplayNodeId = new Map(input.ownerships.map((ownership) => [
    ownership.displayNodeId,
    ownership.ownerNodeId,
  ]))
  const sourceAssetByKey = new Map(input.bodyTopologyAssets.map((asset) => [
    createPuppyBodyTopologyAssetKey(asset),
    asset,
  ]))
  const consumedSourceAssetKeys = new Set<string>()
  const bodyTopologyAssetByKey = new Map<string, PuppyVisibleBodyTopologyAsset>()

  const nodeBindings = input.nodeBindings.map((binding) => {
    const ownerNodeId = ownerNodeIdByDisplayNodeId.get(binding.displayNodeId) ?? binding.assemblyNodeId
    if (!ownerNodeId) {
      return binding
    }

    const bodyBindings = binding.bodyBindings.map((bodyBinding) => {
      const sourceAssetKey = createPuppyBodyTopologyAssetKey(bodyBinding)
      const nextBodyBinding = cloneBodyBindingForDisplayOwner(bodyBinding, ownerNodeId)
      const sourceAsset = sourceAssetByKey.get(sourceAssetKey)
      if (sourceAsset) {
        consumedSourceAssetKeys.add(sourceAssetKey)
        const topologyAsset = cloneBodyTopologyAssetForDisplayOwner({
          asset: sourceAsset,
          bodyBinding: nextBodyBinding,
        })
        bodyTopologyAssetByKey.set(createPuppyBodyTopologyAssetKey(topologyAsset), topologyAsset)
      }
      return nextBodyBinding
    })

    return {
      ...binding,
      assemblyNodeId: ownerNodeId,
      bodyBindings,
    }
  })

  input.bodyTopologyAssets.forEach((asset) => {
    const sourceAssetKey = createPuppyBodyTopologyAssetKey(asset)
    if (!consumedSourceAssetKeys.has(sourceAssetKey)) {
      bodyTopologyAssetByKey.set(sourceAssetKey, asset)
    }
  })

  return {
    nodeBindings,
    bodyTopologyAssets: Array.from(bodyTopologyAssetByKey.values()),
  }
}

/**
 * 把 display / body / geometry 绑定挂到对应 assembly node。
 *
 * @param input 挂载输入。
 */
function attachDisplayBindingsToAssembly(input: {
  readonly assembly: PuppyAssemblyDocument
  readonly nodeBindings: readonly PuppyDisplayNodeBinding[]
  readonly ownerships: readonly PuppyDisplayOwnership[]
}): void {
  const nodeById = new Map(input.assembly.nodes.map((node) => [node.nodeId, node]))
  const ownerNodeIdByDisplayNodeId = new Map(input.ownerships.map((item) => [
    item.displayNodeId,
    item.ownerNodeId,
  ]))

  input.nodeBindings.forEach((binding) => {
    const ownerNodeId = ownerNodeIdByDisplayNodeId.get(binding.displayNodeId) ?? input.assembly.rootNodeId
    const node = nodeById.get(ownerNodeId)
    if (!node) {
      return
    }

    appendUniqueStrings(node.displayNodeIds, [binding.displayNodeId])
    appendUniqueStrings(node.bodyBindingIds, binding.bodyBindings.map((bodyBinding) => bodyBinding.bodyBindingId))
    appendUniqueStrings(node.geometryObjectIds, binding.geometryObjectIds)
  })
}

/**
 * 将权威装配语义源应用到可见模型导出结果。
 *
 * @param exportRoot 可见模型导出结果。
 * @param source 调用方提供的权威装配语义源。
 * @returns 应用语义源后的导出结果。
 */
export function applyPuppyAssemblySemanticSource(
  exportRoot: PuppyVisibleModelExportRoot,
  source: PuppyExportAssemblySemanticSource | null | undefined,
): PuppyVisibleModelExportRoot {
  const hierarchy = resolveExportHierarchy(source)
  if (!hierarchy) {
    return exportRoot
  }

  const buildResult = buildAssemblyDocumentFromHierarchy(
    hierarchy,
    exportRoot.assemblyDocument,
    exportRoot.foldedAssemblyNodes ?? [],
    exportRoot.sourceObjectLocalTransforms ?? {},
  )
  if (!buildResult) {
    return exportRoot
  }

  const ownerships = resolveDisplayOwnerships({
    assembly: buildResult.assembly,
    nodeBindings: exportRoot.nodeBindings,
    ownerNodeIdByBodyKey: buildResult.ownerNodeIdByBodyKey,
  })
  const bindingResult = applyDisplayOwnershipsToExportBindings({
    nodeBindings: exportRoot.nodeBindings,
    bodyTopologyAssets: exportRoot.bodyTopologyAssets,
    ownerships,
  })
  attachDisplayBindingsToAssembly({
    assembly: buildResult.assembly,
    nodeBindings: bindingResult.nodeBindings,
    ownerships,
  })
  new PuppyAssemblyDisplayTreeMaterializer().materialize({
    root: exportRoot.root,
    assembly: buildResult.assembly,
    ownerships,
  })

  return {
    ...exportRoot,
    nodeBindings: bindingResult.nodeBindings,
    bodyTopologyAssets: bindingResult.bodyTopologyAssets,
    assemblyDocument: buildResult.assembly,
    runtimeSourceObjectIdsByAssemblyNodeId: buildResult.runtimeSourceObjectIdsByAssemblyNodeId,
  }
}
